import { Properties } from '../api';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { GuestSurveyStore, guestSurveyStore } from './guest-survey.store';
import { StartGuestSurveyRequest } from '../api/generated/properties/models/start-guest-survey-request';
import { SubmitGuestSurveyRequest } from '../api/generated/properties/models/submit-guest-survey-request';
import { lightFormat } from 'date-fns';
import { dispatchForm } from '../forms';
import { GuestSurveyForms } from './guest-survey.forms';
import { SurveyOutreachFilters, UpdateGuestSurveyOutreachRequest } from './guest-survey.model';
import { applyTransaction, setLoading } from '@datorama/akita';
import { PaginationState } from '../models.common';

export class GuestSurveyService {
  constructor(
    private readonly guestSurveyApi: Properties.GuestSurveyApi,
    private readonly store: GuestSurveyStore
  ) {}

  getGuestSurvey(propertyId: string, surveyId: string) {
    from(this.guestSurveyApi.propertyIdGuestsurveyOutreachSurveyIdGet(surveyId, propertyId))
      .pipe(
        map(({ data }) => {
          return data.data ?? {};
        }),
        setLoading(this.store)
      )
      .subscribe(guestSurvey =>
        applyTransaction(() => {
          this.store.upsert(guestSurvey.id, guestSurvey);
          this.store.setActive(guestSurvey.id);
        })
      );
  }

  getGuestSurveys(
    propertyId: string,
    filters: SurveyOutreachFilters,
    continuationToken?: string,
    limit?: number
  ) {
    from(
      this.guestSurveyApi.propertyIdGuestsurveyOutreachGet(
        propertyId,
        filters.recordNumber,
        filters.outreachCompleted,
        filters.guestId,
        filters.startDate ?? undefined,
        filters.endDate ?? undefined,
        continuationToken,
        limit
      )
    )
      .pipe(
        map(({ data: { data, ...page } }) => {
          return {
            data,
            ...page,
          };
        }),
        setLoading(this.store)
      )
      .subscribe(({ data, ...pagination }) => {
        applyTransaction(() => {
          this.store.upsertMany(data ?? []);
          this.updatePaginationState(pagination);
          this.updateUI(filters);
        });
      });
  }

  startGuestSurvey(propertyId: string, startGuestSurveyRequest: StartGuestSurveyRequest) {
    from(this.guestSurveyApi.propertyIdGuestsurveyStartPost(propertyId, startGuestSurveyRequest))
      .pipe(
        map(({ data }) => {
          return data.data ?? {};
        }),
        dispatchForm(GuestSurveyForms.StartGuestSurvey)
      )
      .subscribe(guestSurvey => {
        applyTransaction(() => {
          this.store.upsert(guestSurvey.id, guestSurvey);
          this.store.setActive(guestSurvey.id);
        });
      });
  }

  submitGuestSurvey(propertyId: string, submitGuestSurveyRequest: SubmitGuestSurveyRequest) {
    from(this.guestSurveyApi.propertyIdGuestsurveySubmitPost(propertyId, submitGuestSurveyRequest))
      .pipe(
        map(({ data }) => {
          return data.data ?? {};
        }),
        dispatchForm(GuestSurveyForms.SubmitGuestSurvey)
      )
      .subscribe(guestSurvey => {
        applyTransaction(() => {
          this.store.upsert(guestSurvey.id, guestSurvey);
          this.store.setActive(guestSurvey.id);
        });
      });
  }

  submitOutreach(propertyId: string, submitOutreachRequest: UpdateGuestSurveyOutreachRequest) {
    from(this.guestSurveyApi.propertyIdGuestsurveyOutreachPut(propertyId, submitOutreachRequest))
      .pipe(
        map(({ data }) => {
          return data.data ?? {};
        }),
        dispatchForm(GuestSurveyForms.UpdateGuestSurvey)
      )
      .subscribe(guestSurvey => {
        applyTransaction(() => {
          this.store.upsert(guestSurvey.id, guestSurvey);
          this.store.setActive(guestSurvey.id);
        });
      });
  }

  exportGuestSurveys(propertyId: string, start?: Date, end?: Date) {
    from(
      this.guestSurveyApi.propertyIdGuestsurveyExportGet(
        propertyId,
        !!start ? lightFormat(start, 'yyyy-MM-dd') : undefined,
        !!end ? lightFormat(end, 'yyyy-MM-dd') : undefined
      )
    )
      .pipe(map(x => new Blob([x.data], { type: 'text/csv' })))
      .subscribe(x =>
        saveAs(
          x,
          `GuestSurveys_${!!start ? lightFormat(start, 'yyyy-MM-dd') : undefined}-${
            !!end ? lightFormat(end, 'yyyy-MM-dd') : undefined
          }.csv`
        )
      );
  }

  private updateUI(state: Partial<SurveyOutreachFilters>) {
    this.store.update(({ ui }) => ({
      ui: { ...ui, ...state },
    }));
  }

  private updatePaginationState({ isDone, continuationToken }: PaginationState) {
    this.store.update(() => ({
      pagination: { isDone, continuationToken },
    }));
  }
}
export const guestSurveyService = new GuestSurveyService(
  new Properties.GuestSurveyApi(),
  guestSurveyStore
);
